import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  Step,
  StepsComponent,
} from '../../../shared/components/steps/steps.component';
import { Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ConfirmarQuantidadeVagasStepComponent } from '../steps/confirmar-quantidade-vagas/confirmar-quantidade-vagas-step.component';
import { AtribuirIneStepComponent } from '../steps/atribuir-ine/atribuir-ine-step.component';
import { ResumoConfirmacaoStepComponent } from '../steps/resumo-confirmacao/resumo-confirmacao-step.component';
import { LoadingService } from '../../../shared/services/loading.service';
import { CommonModule } from '@angular/common';
import { Ciclo } from '../../../shared/models/response/ciclo.model';
import { WebcomponentsAngularModule } from '@govbr-ds/webcomponents-angular';
import { ConfirmacaoVagaService } from '../../../shared/services/confirmacao-vaga.service';
import { AlertService } from '../../../shared/services/alert.service';
import { CardCollapseComponent } from '../../../shared/components/collapse/card-collapse.component';
import { differenceInDays, endOfDay, parse } from 'date-fns';
import { DateUtil } from '../../../shared/utils/date.util';
import { UsuarioResponseDto } from '../../../shared/models/response/usuario-response.model';
import { Municipio } from '../../../shared/models/response/localidade.model';
import { CadastroReservaMunicipioService } from '../../../shared/services/cadastro-reserva-municipio.service';
import { CadastroReservaMunicipio } from '../../../shared/models/response/cadastro-reserva-municipio.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmarCadastroReservaComponent } from '../confirmar-cadastro-reserva/confirmar-cadastro-reserva.component';
import { ResumoConfirmacaoPrint } from '../steps/resumo-confirmacao/resumo-confirmacao-print/resumo-confirmacao-print.component';
import { MENSAGEM_PRAZO_ENCERRADO } from '../confirmacao-vaga.constants';

@Component({
  selector: 'app-confirmacao-vaga-tipo-equipe',
  templateUrl: './confirmacao-vaga-tipo-equipe.component.html',
  standalone: true,
  imports: [
    CommonModule,
    StepsComponent,
    ReactiveFormsModule,
    RouterModule,
    ConfirmarQuantidadeVagasStepComponent,
    AtribuirIneStepComponent,
    ResumoConfirmacaoStepComponent,
    WebcomponentsAngularModule,
    CardCollapseComponent,
  ],
})
export class ConfirmacaoVagaTipoEquipeComponent implements OnInit, OnChanges {
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly alertService = inject(AlertService);
  private readonly loadingService = inject(LoadingService);
  private readonly confirmacaoVagaService = inject(ConfirmacaoVagaService);
  private readonly cadastroReservaMunicipioService = inject(CadastroReservaMunicipioService);
  private readonly modalService = inject(NgbModal);

  @Input() cicloSelected: Ciclo | null = null;
  @Input() usuario!: UsuarioResponseDto;
  @Input() municipio: Municipio | null = null;

  onLoadModalidadeVagas: EventEmitter<any[]> = new EventEmitter<any[]>();
  onStepChange: EventEmitter<number> = new EventEmitter<number>();
  onConfirmarQuantidades: EventEmitter<void> = new EventEmitter<void>();
  onAtribuirINE: EventEmitter<void> = new EventEmitter<void>();
  onResumoConfirmacao: EventEmitter<void> = new EventEmitter<void>();
  onCancel: EventEmitter<void> = new EventEmitter<void>();
  onSubmitting: EventEmitter<boolean> = new EventEmitter<boolean>();
  onPrintCadastroReserva: EventEmitter<void> = new EventEmitter<void>();

  currentStep: number = 1;
  form!: FormGroup;
  confirmacaoVaga: any = null;
  modalidadeVagas: any[] = [];
  isVagasLoaded: boolean = false;
  hasCadastroReserva: boolean = false;
  cadastrosReservaMunicipio: CadastroReservaMunicipio[] = [];

  public steps: Step[] = [
    { number: 1, title: 'Confirmar Quantidades', active: true, completed: false, disabled: false },
    { number: 2, title: 'Atribuir INEs', active: false, completed: false, disabled: true },
    { number: 3, title: 'Resumo e Confirmação', active: false, completed: false, disabled: true },
  ];

  constructor() {
    this.buildForm();
  }

  ngOnInit(): void {
    this.onStepChange.subscribe((stepNumber) => {
      this.currentStep = stepNumber;
      this.cdr.detectChanges();
      if (stepNumber === 1) {
        this.onLoadModalidadeVagas.emit(this.modalidadeVagas);
      }
    });

    this.onPrintCadastroReserva.subscribe(() => {
      const modalRef = this.modalService.open(ResumoConfirmacaoPrint, { size: 'lg' });
      modalRef.componentInstance.modalRef = modalRef;
      modalRef.componentInstance.cadastrosReservaMunicipio = this.cadastrosReservaMunicipio;
    });

    this.onConfirmarQuantidades.subscribe(async (data: any) => {
      if (this.isAvancoBloqueadoPorPrazo()) return;
      if (this.cadastrosReservaMunicipio.filter((c) => c.dtConfirmacao == null).length > 0) {
        this.confirmarCadastroReserva();
        return;
      }
      if (this.isPeriodoVigente()) {
        await this.saveRascunho(data);
      } else {
        this.onSubmitting.emit(false);
      }
      this.navigateToStep(this.currentStep);
      this.stepChange(this.currentStep);
    });

    this.onAtribuirINE.subscribe(async (data: any) => {
      if (this.isAvancoBloqueadoPorPrazo()) return;
      if (this.isPeriodoVigente()) {
        await this.saveAtribuicaoIne(data);
      } else {
        this.onSubmitting.emit(false);
      }
      this.navigateToStep(this.currentStep);
      this.stepChange(this.currentStep);
    });

    this.onResumoConfirmacao.subscribe(async () => {
      if (this.isAvancoBloqueadoPorPrazo()) return;
      await this.saveConfirmacao();
    });

    this.onCancel.subscribe(() => {
      this.router.navigate(['/']);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cicloSelected'] && this.cicloSelected) {
      this.resetData();
      this.loadData();
    } else if (changes['cicloSelected'] && !this.cicloSelected) {
      this.resetData();
    }
  }

  get formConfirmarQuantidadeVaga(): FormGroup {
    return this.form.get('confirmarQuantidadeVaga') as FormGroup;
  }

  get formAtribuirINE(): FormGroup {
    return this.form.get('atribuirINE') as FormGroup;
  }

  stepChange(stepNumber: number): void {
    this.currentStep = stepNumber;
    this.cdr.detectChanges();
    this.onStepChange.emit(stepNumber);
  }

  clickStep(step: Step): void {
    if (step.disabled) return;
    if (step.number === 1) {
      this.onLoadModalidadeVagas.emit(this.modalidadeVagas);
    }
    this.onStepChange.emit(step.number);
  }

  isPeriodoVigente(ignoreChecks: boolean = false): boolean {
    if (!ignoreChecks && this.modalidadeVagas.length === 0) return false;
    if (!ignoreChecks && this.confirmacaoVaga && !this.confirmacaoVaga.rascunho) return false;
    if (this.cicloSelected && this.cicloSelected.confirmacaoVaga) {
      const now = new Date();
      const dtFim = endOfDay(parse(this.cicloSelected.confirmacaoVaga.fim, 'yyyy-MM-dd', new Date()));
      return now <= dtFim;
    }
    return false;
  }

  // Confirmação já finalizada continua navegável (somente leitura) após o prazo.
  private isAvancoBloqueadoPorPrazo(): boolean {
    const confirmacaoFinalizada = this.confirmacaoVaga?.rascunho === false;
    if (confirmacaoFinalizada || this.isPeriodoVigente(true)) return false;

    this.onSubmitting.emit(false);
    this.alertService.error(MENSAGEM_PRAZO_ENCERRADO);
    return true;
  }

  diasRestantes(): number {
    if (this.cicloSelected && this.cicloSelected.confirmacaoVaga) {
      const now = new Date();
      const dtFim = new Date(this.cicloSelected.confirmacaoVaga.fim);
      return differenceInDays(endOfDay(dtFim), now);
    }
    return 0;
  }

  confirmarCadastroReserva(): void {
    const modalRef = this.modalService.open(ConfirmarCadastroReservaComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.modalRef = modalRef;
    modalRef.componentInstance.isPeriodoVigente = this.isPeriodoVigente(true);
    modalRef.componentInstance.usuario = this.usuario;
    modalRef.componentInstance.cadastrosReservaMunicipio = this.cadastrosReservaMunicipio;
    modalRef.result
      .then((resultado) => {
        if (Array.isArray(resultado)) {
          this.cadastrosReservaMunicipio = resultado;
          this.hasCadastroReserva = true;
          this.cdr.detectChanges();
        }
      })
      .catch(() => {});
  }

  async saveRascunho(data: any): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.confirmacaoVagaService
        .saveRascunho({ cicloId: this.cicloSelected?.id, ...data })
        .subscribe({
          next: (response) => {
            this.confirmacaoVaga = response;
            this.onSubmitting.emit(false);
            this.cdr.detectChanges();
            resolve();
          },
          error: (error) => {
            this.onSubmitting.emit(false);
            this.alertService.error(error?.error?.detail || 'Erro ao salvar. Tente novamente.');
            reject();
          },
        });
    });
  }

  async saveAtribuicaoIne(data: any): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.confirmacaoVagaService
        .saveAtribuicaoIne({
          cicloId: this.cicloSelected?.id,
          vagas: this.confirmacaoVaga.conteudo,
          ...data,
        })
        .subscribe({
          next: (response) => {
            this.confirmacaoVaga = response;
            this.onSubmitting.emit(false);
            this.cdr.detectChanges();
            resolve();
          },
          error: (error) => {
            this.alertService.error(error?.error?.detail || 'Erro ao salvar. Tente novamente.');
            this.onSubmitting.emit(false);
            reject();
          },
        });
    });
  }

  async saveConfirmacao(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.confirmacaoVagaService.saveConfirmacao(this.confirmacaoVaga).subscribe({
        next: (response) => {
          this.confirmacaoVaga = response;
          this.onSubmitting.emit(false);
          this.steps[2].completed = true;
          this.cdr.detectChanges();
          this.alertService.success('Vagas confirmadas com sucesso.');
          resolve();
        },
        error: (error) => {
          this.alertService.error(error?.error?.detail || 'Erro ao salvar. Tente novamente.');
          this.onSubmitting.emit(false);
          reject();
        },
      });
    });
  }

  private loadData(): void {
    if (!this.cicloSelected) return;
    const cicloId = this.cicloSelected.id;
    this.loadReserva(cicloId).then(() => {
      this.loadConfirmacao(cicloId).then(() => {
        this.loadModalidadeVagas(cicloId);
        if (
          this.isPeriodoVigente(true) &&
          this.cadastrosReservaMunicipio.filter((c) => c.dtConfirmacao == null).length > 0
        ) {
          this.confirmarCadastroReserva();
        }
      });
    });
  }

  private async loadConfirmacao(cicloId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.loadingService.open();
      this.confirmacaoVagaService.findByCicloAndGestor(cicloId).subscribe({
        next: (response) => {
          this.loadingService.close();
          if (response) {
            this.confirmacaoVaga = response;
            if (!this.confirmacaoVaga.rascunho) {
              this.steps = this.steps.map((step) => ({ ...step, disabled: false, completed: true }));
            } else if (this.confirmacaoVaga?.confirmacoesVagaIne?.length > 0) {
              this.steps = this.steps.map((step: any, index: number) => {
                if (index < 2) { step.disabled = false; step.completed = true; }
                return step;
              });
            } else if (this.confirmacaoVaga) {
              this.steps[0].completed = true;
              this.steps[0].disabled = false;
            }
            this.cdr.detectChanges();
          }
          resolve();
        },
        error: (error) => {
          this.loadingService.close();
          if (error?.detail) {
            this.alertService.error(error.detail);
          } else {
            this.alertService.error('Erro ao carregar dados. Tente novamente.');
          }
          reject();
        },
      });
    });
  }

  private loadModalidadeVagas(cicloId: number): void {
    this.loadingService.open();
    this.confirmacaoVagaService.fetchTotalVagasConfirmacao(cicloId).subscribe({
      next: (response) => {
        this.loadingService.close();
        const modalidadeVagas: any[] = [];
        response.forEach((item) => {
          const idx = modalidadeVagas.findIndex((mv) => mv.idModalidade === item.idModalidade);
          if (idx === -1) {
            modalidadeVagas.push({
              idModalidade: item.idModalidade,
              modalidade: item.modalidade,
              data: [{ tipoEquipe: item.tipoEquipe, totalVagas: item.totalVagas }],
            });
          } else {
            modalidadeVagas[idx].data.push({ tipoEquipe: item.tipoEquipe, totalVagas: item.totalVagas });
          }
        });
        this.isVagasLoaded = true;
        this.modalidadeVagas = modalidadeVagas;
        this.onLoadModalidadeVagas.emit(modalidadeVagas);
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.loadingService.close();
        this.alertService.error(error?.detail || 'Erro ao carregar vagas. Tente novamente.');
      },
    });
  }

  private async loadReserva(cicloId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.loadingService.open();
      this.cadastroReservaMunicipioService
        .fetchByMunicipioAndCiclo(this.municipio?.id as string, cicloId)
        .subscribe({
          next: (cadastros: CadastroReservaMunicipio[]) => {
            this.loadingService.close();
            this.cadastrosReservaMunicipio = cadastros;
            this.hasCadastroReserva = cadastros.filter((c) => c.dtConfirmacao != null).length > 0;
            this.cdr.detectChanges();
            resolve();
          },
          error: (error) => {
            this.loadingService.close();
            this.alertService.error(error?.detail || 'Erro ao carregar cadastro de reserva. Tente novamente.');
            reject();
          },
        });
    });
  }

  private buildForm(): void {
    this.form = this.formBuilder.group({
      confirmarQuantidadeVaga: this.formBuilder.group({
        modalidadeVaga: this.formBuilder.array([]),
        vagasIne: this.formBuilder.array([]),
        cadastroReserva: this.formBuilder.array([]),
      }),
      atribuirINE: this.formBuilder.group({
        vagasEquipe: this.formBuilder.array([]),
      }),
    });
  }

  private resetSteps(): void {
    this.steps = this.steps.map((step) => ({
      ...step,
      active: step.number === 1,
      completed: false,
      disabled: step.number !== 1,
    }));
    this.currentStep = 1;
  }

  private resetData(): void {
    this.confirmacaoVaga = null;
    this.modalidadeVagas = [];
    this.isVagasLoaded = false;
    this.hasCadastroReserva = false;
    this.cadastrosReservaMunicipio = [];
    this.buildForm();
    this.resetSteps();
    this.cdr.detectChanges();
  }

  private navigateToStep(stepNumber: number, asc: boolean = true): void {
    const from = stepNumber - (asc ? 1 : -1);
    this.steps[from].active = false;
    this.steps[from].completed = true;
    this.steps[stepNumber].active = true;
    this.steps[stepNumber].disabled = false;
    this.currentStep = (asc ? stepNumber + 1 : stepNumber - 1) as number;
  }
}
